import { useEffect, useRef, useState, forwardRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Header from "../header/header";
import { useGame } from "../header/GameContext";
import { CHAT_DATA } from "./chatData";
import "./chat.css";

const MSG_INTERVAL = 1000; // 메시지 간격 (ms) - 원하는 값으로 조절
const BTN_DELAY = 100;

function ChatBox(
  { onFinish, defaultPath, timerMode: timerModeFromProps },
  ref,
) {
  const location = useLocation();
  const {
    startTimer,
    pauseTimer,
    isFinalQuiz,
    playerName,
    userRole,
    increaseMissionOnce,
  } = useGame();

  const state = location.state || {};
  const { DateVal, titImgClass, timerMode: timerModeFromState } = state;
  const timerMode = timerModeFromState ?? timerModeFromProps;

  /* ========================= 타이머 ========================= */
  useEffect(() => {
    timerMode === "run" ? startTimer() : pauseTimer();
  }, [timerMode]); // eslint-disable-line

  /* ========================= 데이터 =========================
     🔥 핵심: useMemo로 contents를 고정 — 매 렌더마다 새 배열 생성 방지
  ========================================================== */
  const playerRole = userRole === "agentY" ? "Agent Y" : "Agent X";

  const contents = useMemo(() => {
    const rawContents = Array.isArray(DateVal) ? DateVal : CHAT_DATA[DateVal];
    if (!rawContents) return [];
    return rawContents.map((item) => ({
      ...item,
      text: item.text
        ?.replace(/\{\{playerName\}\}/g, playerName || "")
        .replace(/\{\{playerRole\}\}/g, playerRole || ""),
    }));
  }, [DateVal, playerName, playerRole]); // DateVal이 바뀔 때만 재계산

  /* ========================= 상태 ========================= */
  const [visible, setVisible] = useState([]);
  const [activeButton, setActiveButton] = useState(null);
  const nextIdxRef = useRef(0);
  const chatBoxRef = useRef(null);
  const timersRef = useRef([]); // 🔥 타이머 추적용

  /* ========================= 스크롤 ========================= */
  useEffect(() => {
    if (!chatBoxRef.current) return;
    const box = chatBoxRef.current;
    box.scrollTop = box.scrollHeight;
  }, [visible]);

  /* ========================= 타이머 클린업 ========================= */
  const clearAllTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  /* ========================= 🔥 메시지 스케줄링 ========================= */
  const scheduleMessages = (startIdx) => {
    clearAllTimers(); // 기존 타이머 전부 제거 후 새로 등록

    let delay = 0;

    for (let i = startIdx; i < contents.length; i++) {
      const item = contents[i];

      if (item.type === "scroll") {
        const t = setTimeout(() => {
          window.scrollTo(0, document.body.scrollHeight);
        }, delay);
        timersRef.current.push(t);
        continue; // delay 증가 없음
      }

      if (item.type === "button") {
        const btnIdx = i;
        const t = setTimeout(() => {
          setActiveButton(item);
          nextIdxRef.current = btnIdx + 1;
          requestAnimationFrame(() => {
            window.scrollTo({
              top: document.documentElement.scrollHeight,
              behavior: "smooth",
            });
          });
        }, delay + BTN_DELAY);
        timersRef.current.push(t);
        break; // 버튼 이후는 클릭 시 재개
      }

      // 일반 채팅
      const t = setTimeout(() => {
        setVisible((prev) => [...prev, item]);
      }, delay);
      timersRef.current.push(t);
      delay += MSG_INTERVAL;
    }
  };

  /* ========================= 마운트 시 최초 1회 실행 ========================= */
  useEffect(() => {
    if (contents.length === 0) return;
    nextIdxRef.current = 0;
    setVisible([]);
    setActiveButton(null);
    scheduleMessages(0);

    return () => clearAllTimers(); // 언마운트 시 전부 정리
  }, [contents]); // 🔥 contents가 useMemo로 고정되어 있어 한 번만 실행됨

  /* ========================= 버튼 클릭 ========================= */
  const onBtnClick = () => {
    if (!activeButton) return;
    const { action, route, state } = activeButton;
    setActiveButton(null);

    if (nextIdxRef.current < contents.length) {
      scheduleMessages(nextIdxRef.current);
    }

    const actionMap = {
      finish: () => onFinish?.({ route, defaultPath, state }),
      route: () => {
        if (DateVal === "intro2") increaseMissionOnce("suspect");
        onFinish?.({ route, defaultPath, state });
      },
    };
    actionMap[action]?.();
  };

  /* ========================= 렌더 ========================= */
  return (
    <div className="box content_box1">
      <Header isFinalQuiz={isFinalQuiz(DateVal)} />

      <div className="box_con con_box1 con_pd5 ans0001">
        <div className={`mission_tit ${titImgClass}`} />

        <div
          className="chat_box2 mt24"
          ref={chatBoxRef}
          style={{ overflowY: "auto", maxHeight: "400px" }}
        >
          {visible.map((c, i) => {
            if (c.type === "chatInfo_Talk1") {
              return (
                <div
                  key={i}
                  className={`chatInfo_Talk ${c.marType || ""}`}
                  dangerouslySetInnerHTML={{ __html: c.text }}
                />
              );
            }
            if (c.type === "leftCharacter") {
              return (
                <div key={i} className={`chat_b_left ${c.marType || ""}`}>
                  <div className="chat_b_l_left mr8">
                    <span className={c.chat_icon} />
                  </div>
                  <div className="chat_b_l_right">
                    <p className="chat_tit mb4">{c.chat_tit}</p>
                    <div
                      className={c.talkType}
                      dangerouslySetInnerHTML={{ __html: c.text }}
                    />
                  </div>
                </div>
              );
            }
            if (c.type === "left_txt") {
              return (
                <div key={i} className={`chat_b_left ${c.marType || ""}`}>
                  <div className="chat_b_l_left mr8" />
                  <div className="chat_b_l_right">
                    <div
                      className={c.talkType}
                      dangerouslySetInnerHTML={{ __html: c.text }}
                    />
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>

        {activeButton && (
          <div className="chat_btn_area mt20">
            <button className="inputBtn02" onClick={onBtnClick}>
              {activeButton.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default forwardRef(ChatBox);
